import { ApprovalStatus } from '@common/model/approval-status';
import { RegistrationBase } from '@common/model/registration.base';
import { Supplier } from '@common/model/supplier.base';
import { PaymentStatus } from '@common/model/payment';
import { HouseType } from './house-type';

export class Contact {
    department: string;
    jobTitle: string;

    nameInKanji: string;
    givenNameInKanji: string;
    nameInKatakana: string;
    givenNameInKatakana: string;

    zip: string;
    prefecture: string;
    city: string;
    blockAndHouseNr: string;
    buildingName: string;

    englishPrefecture: string;
    englishSublocalities: string;
    englishWard: string;

    isContractAddressTheSameAsInstallation: boolean;

    installationZip: string;
    installationPrefecture: string;
    installationCity: string;
    installationBlockAndHouseNr: string;
    installationBuildingName: string;

    installationEnglishPrefecture: string;
    installationEnglishSublocalities: string;
    installationEnglishWard: string;

    insideRestrictedArea?: YesNoDontKnow;
    snowfallArea?: SnowfallArea;

    phone: string;
    cellPhone: string;

    agreesToConditions?: boolean;
    allowsContact?: boolean;
    companyName: string;
    businessType?: BusinessType;
    registrationReason?: RegistrationReason;
    multipleBuildings?: boolean;

    interestedInFinancing?: boolean;
}

export class Product {
    // Solar Fields
    hasPanelsAlready?: boolean;

    houseType?: HouseType;
    numberOfStories?: NumberOfStories;
    houseAge?: HouseAge;
    hasRoofBeenRenovated?: boolean;
    roofType?: RoofType;
    roofMaterial?: RoofMaterial;
    hasInstallationEstimate?: boolean;
    hasEstimatedCapacity?: boolean;
    estimatedCapacity?: number;
    knowsRoofSize?: boolean;
    roofDimensionA?: number;
    roofDimensionB?: number;

    // B2B fields
    hasHighVoltage?: boolean;
    contractedElectricityAmount?: number;
    annualElectricityUsage?: AnnualElectricityUsage;
    isForThirdCompany?: boolean;
    interestedInPPA?: boolean;

    panelsProducer?: PanelsProducers;
    panelsProducerOtherName?: string;
    panelsCapacity?: number;
    indicationsCapacity?: number;
    panelsInstallationYear?: number;

    // Battery Fields
    wantsToUseBattery?: YesNoDontKnow;
    batteryType?: BatteryType;
    batterySize?: BatterySize;
    hasEvorPHV?: boolean;

    // ***** Legacy fields (not in use by current version) *****
    roofPitch?: RoofPitch;
    roofOrientation?: RoofOrientation;

    roofDimensionC?: number;
    numberOfPanelsThatFitOnRoof?: number;
    chosenNumberOfPanels?: number;

    hasRoofObjects?: boolean;
    hasRoofShadow?: boolean;
    hasRoofWithMultipleParts?: boolean;
    roofParts?: RoofParts;

    constructionAreaMeters: number;
    constructionAreaTsubo: number;

    lookingForValue?: LookingForValue;
    notes?: string;
    interestedInBattery?: YesNoDontKnow;
    // ****************
}

export class Proposal {
    // Solar Fields
    panelType?: SolarPanelType;
    numberOfPanels?: number;
    capacity?: number;
    price?: number; // TODO: rename to solarPrice
    paybackTime?: number;
    supplier?: Supplier;
    firstYearSaving?: number;
    co2ReductionKg?: number;
    co2ReductionOfAverage?: number;

    // Battery Fields
    batteryType?: BatteryType;
    batterySize?: BatterySize;
    batteryPower?: number;
    batteryPrice: number;
}

export class Decision {
    reasonToDecline?: string;
    reasonToDeclineDescription?: string;
    notesToSupplier?: string;
    agreesToSupplierConditions?: boolean;
    agreesToDeposit?: boolean;
}

export class Survey {
    datePreference1Utc?: Date;
    datePreference2Utc?: Date;
    datePreference3Utc?: Date;
    dayPreference?: SurveyDayPreference;
}

export class Delivery {
    // string representation of Nullable<DateTimeOffset> actually (but we don't need the extra fidelity yet
    // so we type it as a string in frontend for now)
    cancellationRequestedByConsumer: string;
    cancellationReason: string;
    deliveryCancellationStatus: ApprovalStatus;
    consumerCancellationExplanation: string;
}

export class Registration extends RegistrationBase {
    flowType: FlowType;
    contact: Contact;
    product: Product;
    proposal: Proposal;
    decision: Decision;
    survey: Survey;
    delivery: Delivery;

    excluded: boolean;
    isOfflinePayment: boolean;
    paymentInstructionsUrl: string;
    paymentIntentId: string;

    constructor() {
        super();
        this.contact = new Contact();
        this.product = new Product();
        this.proposal = new Proposal();
        this.decision = new Decision();
        this.survey = new Survey();
    }

    get isOpenForPayment(): boolean {
        return (
            this.decisionComplete &&
            this.proposalAccepted &&
            !this.cancelled &&
            !this.excluded &&
            this.auction &&
            this.auction.openForPayment
        );
    }

    get isReadyForPayment(): boolean {
        return (
            this.isOpenForPayment &&
            (!this.paymentStatus ||
                this.paymentStatus !== PaymentStatus.Successful)
        );
    }

    get isPaymentSuccessful(): boolean {
        return (
            this.isOpenForPayment &&
            this.paymentStatus &&
            this.paymentStatus === PaymentStatus.Successful
        );
    }

    get isPendingOfflinePayment(): boolean {
        return (
            this.isOpenForPayment &&
            this.isOfflinePayment &&
            this.paymentStatus === PaymentStatus.Pending
        );
    }
}

export namespace Registration {
    export function isPersonalDataReady(registration: Registration): boolean {
        return (
            registration &&
            registration.contact &&
            registration.contact.zip !== null &&
            registration.contact.zip !== undefined &&
            registration.email !== null &&
            registration.email !== undefined
        );
    }

    export function isProductDataReady(registration: Registration): boolean {
        return (
            registration &&
            registration.product &&
            (isPanelProductReady(registration) ||
                isBatteryProductReady(registration))
        );
    }

    function isPanelProductReady(registration: Registration): boolean {
        return (
            !isBatteryProductReady(registration) &&
            registration.product.roofType !== null &&
            registration.product.roofType !== undefined &&
            registration.product.roofMaterial !== null &&
            registration.product.roofMaterial !== undefined
        );
    }
    function isBatteryProductReady(registration: Registration): boolean {
        return (
            registration.product.hasPanelsAlready === true &&
            registration.product.panelsCapacity !== null &&
            registration.product.panelsCapacity !== undefined
        );
    }
}

export enum FlowType {
    Legacy = 'Legacy',
    SolarPanels = 'SolarPanels',
    SolarPanelsAndBattery = 'SolarPanelsAndBattery',
    Battery = 'Battery',
    BatteryWithoutSolarPanels = 'BatteryWithoutSolarPanels'
}
export namespace FlowType {
    export function fromString(flowType: string): FlowType {
        switch (flowType) {
            case 'SolarPanels':
                return FlowType.SolarPanels;
            case 'SolarPanelsAndBattery':
                return FlowType.SolarPanelsAndBattery;
            case 'Battery':
                return FlowType.Battery;
            case 'BatteryWithoutSolarPanels':
                return FlowType.BatteryWithoutSolarPanels;
            default:
                return FlowType.Legacy;
        }
    }
}

export enum RoofPitch {
    ZeroDegrees = 'ZeroDegrees',
    TenDegrees = 'TenDegrees',
    TwentyDegrees = 'TwentyDegrees',
    ThirtyDegrees = 'ThirtyDegrees',
    SixtyDegrees = 'SixtyDegrees',
}

export enum RoofOrientation {
    North = 'North',
    NorthEast = 'NorthEast',
    East = 'East',
    SouthEast = 'SouthEast',
    South = 'South',
    SouthWest = 'SouthWest',
    West = 'West',
    NorthWest = 'NorthWest',
}

export enum RoofType {
    FlatRoof = 'FlatRoof',
    InclinedRoof = 'InclinedRoof',
    HipRoof = 'HipRoof',
    PyramidHipRoof = 'PyramidHipRoof',
    MShapedRoof = 'MShapedRoof',
    MetalWavyRoof = 'MetalWavyRoof',
    Others = 'Others',
}

export enum RoofMaterial {
    Tiles = 'Tiles',
    Slate = 'Slate',
    Metal = 'Metal',
    Flat = 'Flat',
    Other = 'Other',
}

export enum LegalEntityType {
    Private = 'Private',
    Company = 'Company',
    NonProfitAssociation = 'NonProfitAssociation',
}

export enum NumberOfStories {
    SingleStory = 'SingleStory',
    DoubleStory = 'DoubleStory',
    TripleStory = 'TripleStory',
    FourOrMore = 'FourOrMore',
    ThreeOrMore = 'ThreeOrMore',
}

export enum LookingForValue {
    Looks = 'Looks',
    SmartValue = 'SmartValue',
}

export enum RoofParts {
    OnePart = 'OnePart',
    TwoParts = 'TwoParts',
    ThreeParts = 'ThreeParts',
    FourParts = 'FourParts',
    FiveOrMore = 'FiveOrMore',
}

export enum HouseAge {
    BelowTen = 'BelowTen',
    BetweenTenAndTwenty = 'BetweenTenAndTwenty',
    BetweenTwentyAndThirty = 'BetweenTwentyAndThirty',
    AboveThirty = 'AboveThirty',
    DontKnow = 'DontKnow',
    BelowThirty = 'BelowThirty',
    Year2000AndNewer = 'Year2000AndNewer',
    Year1981To1999 = 'Year1981To1999',
    Year1980AndOlder = 'Year1980AndOlder',
}

export enum AnnualElectricityUsage {
    Below100000 = 'Below100000',
    Between100000and500000 = 'Between100000and500000',
    Between500001and1000000 = 'Between500001and1000000',
    Between1000001and3000000 = 'Between1000001and3000000',
    Between3000001and5000000 = 'Between3000001and5000000',
    MoreThan5000001 = 'MoreThan5000001',
    DontKnow = 'DontKnow',
}

export enum ReasonToDeclineOption {
    NoReason = 'NoReason',
    RoofNotFit = 'RoofNotFit',
    NoTime = 'NoTime',
    PersonalFinancialSituation = 'PersonalFinancialSituation',
    OtherRenovations = 'OtherRenovations',
    UncertaintyInMarket = 'UncertaintyInMarket',
    NoTrustInSupplier = 'NoTrustInSupplier',
    ReceivedBetterOffer = 'ReceivedBetterOffer',
    WantsOtherProducts = 'WantsOtherProducts',
    PaybackTimeTooLong = 'PaybackTimeTooLong',
    OtherReason = 'OtherReason',
    CoronaPandemic = 'CoronaPandemic',
}

export enum YesNoDontKnow {
    Yes = 'Yes',
    No = 'No',
    DontKnow = 'DontKnow',
}

export enum PanelsProducers {
    Choshu = 'Choshu',
    Sharp = 'Sharp',
    Panasonic = 'Panasonic',
    Kyocera = 'Kyocera',
    Mitsubishi = 'Mitsubishi',
    CanadianSolar = 'CanadianSolar',
    Hanfa = 'Hanfa',
    Toshiba = 'Toshiba',
    SolarFrontier = 'Solar Frontier',
    Sanyo = 'Sanyo',
    // Mandatory value!!! There is direct casting + logic for "Other" value inside API so please don't change it.
    Other = 'Other',
}

export enum SurveyDayPreference {
    Weekdays = 'Weekdays',
    Weekends = 'Weekends',
    Unspecified = 'Unspecified',
}

export enum SolarPanelType {
    SmartValue = 'SmartValue',
    GoodLooking = 'GoodLooking',
    Unspecified = 'Unspecified',
}

export enum BatteryType {
    Single = 'Single',
    Hybrid = 'Hybrid',
}

export enum BatterySize {
    Small = 'Small',
    Big = 'Big',
}

export enum SnowfallArea {
    NoSnowfall = 'NoSnowfall',
    Snowfall = 'Snowfall',
    HeavySnowfall = 'HeavySnowfall',
    SuperHeavySnowfall = 'SuperHeavySnowfall',
}

export enum BusinessType {
    ManufactureOil = 'ManufactureOil',
    ManufactureLumber = 'ManufactureLumber',
    ManufactureMetal = 'ManufactureMetal',
    ManufactureOther = 'ManufactureOther',
    TransportWarehouse = 'TransportWarehouse',
    RetailerWholesaler = 'RetailerWholesaler',
    Educational = 'Educational',
    Medical = 'Medical',
    Others = 'Others',
    WelfareRelated = 'WelfareRelated',
}

export enum RegistrationReason {
    ExploringSolarOptions = 'ExploringSolarOptions',
    EvaluatingSolarProvidersAndSolutions = 'EvaluatingSolarProvidersAndSolutions',
    WantsToInstallAsap = 'WantsToInstallAsap',
    LookingForInformation = 'LookingForInformation',
    LookingForQuote = 'LookingForQuote',
    Other = 'Other',
}
